/* ================================================================
   SORENNA — sorenna-motion.js
   Scroll-reveal (.rv pattern) + trial-results ring fill.
   Loaded deferred from layout/theme.liquid.
   Honours prefers-reduced-motion by doing nothing at all —
   content is fully visible without JS or with motion disabled.
   Skips the Shopify theme editor so editing stays static.
   ================================================================ */
(function () {
  'use strict';

  var REVEAL_SELECTORS = [
    '.sorenna-hero__inner > *',
    '.sorenna-trust-strip__item',
    '.sorenna-product-truth__inner > *',
    '.sorenna-story-band__inner > *',
    '.sorenna-ugc__card',
    '.sorenna-final-cta__inner > *',
    '.srn-about-hero__inner > *',
    '.srn-about-story__inner > *',
    '.srn-about-honesty__inner > *',
    '.srn-about-cta__inner > *',
    '.snn-wrap > *',
    '.sorenna-faq__heading',
    '.sorenna-faq__intro',
    '.sorenna-faq__trust',
    '.sorenna-faq__item',
    '.sorenna-faq__cta',
    '.sct-contact__inner > *',
    '.s404__inner > *',
    '.sro-inner > *'
  ];

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isAlreadyOnScreen(el) {
    var rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight * 0.92 && rect.bottom > 0;
  }

  function initReveal() {
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;
    if (window.Shopify && window.Shopify.designMode) return;

    var els = [];
    REVEAL_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (el.classList.contains('rv')) return;
        el.classList.add('rv');
        els.push(el);
      });
    });
    if (!els.length) return;

    // Stagger siblings: 70ms per revealed sibling, capped at 350ms
    els.forEach(function (el) {
      var i = 0;
      var sib = el.previousElementSibling;
      while (sib) {
        if (sib.classList.contains('rv')) i += 1;
        sib = sib.previousElementSibling;
      }
      el.style.setProperty('--rv-delay', Math.min(i, 5) * 70 + 'ms');
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    els.forEach(function (el) {
      if (isAlreadyOnScreen(el)) {
        // Already on screen at load: show immediately, never hide content
        el.classList.add('in');
      } else {
        io.observe(el);
      }
    });
  }

  /* ----------------------------------------------------------------
     Trial-results rings.

     FIX (Jul 2026 — reported: rings frozen/never fill on mobile, and
     reload-mid-scroll showed the correct ring then flipped it to a
     near-empty dot that never recovered):

     1. This function previously reset EVERY ring to a near-empty dot
        unconditionally, then relied on the IntersectionObserver firing
        to restore it — with no "already visible at load" check, unlike
        initReveal() above. On a reload where the ring is already in
        view (mobile browsers still settling the viewport as the
        address bar resizes), that restore could be missed entirely,
        leaving the ring stuck at the dot. Now mirrors initReveal()'s
        pattern: if the ring is already on screen when the script runs,
        it's drawn straight to its correct value — no reset-to-zero
        flash, nothing to "catch up" on.

     2. The fill was driven by a CSS transition on the SVG
        `stroke-dasharray` attribute, set via arc.setAttribute(...).
        Chrome/Firefox will transition a presentation attribute changed
        this way; Safari/WebKit (all iOS browsers) does not reliably —
        it treats attribute mutation differently from a style-property
        change, so the fill either snaps instantly or never repaints
        until an unrelated reflow. That's the "rings don't move on my
        phone" report. Replaced with a manual requestAnimationFrame
        tween that writes the attribute value every frame — this is
        plain JS math, so there is no browser-specific CSS-transition
        behaviour to fail. The matching CSS transition rule on
        .snn-ring__arc in sorenna-system.css has been removed so the
        two don't fight each other.

     FIX (Aug 2026 — reported: rings never animate on the PDP
     (templates/product.json), animate correctly on the sales page
     (templates/product.sales-page.json), same section file on both):

     The "already on screen" check above ran at script-init time —
     effectively DOMContentLoaded, since this script is loaded
     deferred — before the hero gallery's images have finished loading
     and decoding. Until they do, the browser's layout for everything
     above the trial section is provisional, not final.

     Section order puts the trial block 5th on the PDP (hero, certs,
     why_nothing, mechanism, trial) but 6th on the sales page (hero,
     reviews, certs, why_nothing, guarantee, trial) — the sales page
     has a full extra review carousel and guarantee block stacked above
     it. That makes the PDP's trial section the one sitting close
     enough to the fold that a transient, not-yet-settled layout height
     during image load can misreport it as already visible, where the
     sales page's copy of the same section is far enough down the page
     that the same transient error never brings it inside the
     threshold either way.

     Fix: defer the on-screen measurement itself to the window `load`
     event (or run immediately if the page has already finished loading
     by the time this executes), so it always reads final, settled
     layout — hero images decoded, no pending reflow. This changes
     nothing for a ring that is genuinely far down the page (PDP or
     sales page): it was correctly measured as off-screen before and
     still is now, and still gets the scroll-triggered animation via
     the IntersectionObserver below, which is set up before this
     measurement runs either way. It only changes the outcome for a
     ring whose on-screen status was previously misread during the
     loading window.
     ---------------------------------------------------------------- */
  var RING_CIRCUMFERENCE = 339.3;
  var RING_DURATION = 1500; // ms — matches the previous CSS transition duration
  var RING_DELAY = 150;     // ms — matches the previous CSS transition delay

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function animateRingArc(arc, toLength) {
    var startTime = null;
    function step(timestamp) {
      if (startTime === null) startTime = timestamp;
      var elapsed = timestamp - startTime - RING_DELAY;
      if (elapsed < 0) {
        window.requestAnimationFrame(step);
        return;
      }
      var progress = Math.min(elapsed / RING_DURATION, 1);
      var eased = easeOutCubic(progress);
      var current = toLength * eased;
      arc.setAttribute('stroke-dasharray', current.toFixed(1) + ' ' + RING_CIRCUMFERENCE);
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    }
    window.requestAnimationFrame(step);
  }

  function initRings() {
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;
    if (window.Shopify && window.Shopify.designMode) return;

    var arcs = document.querySelectorAll('.snn-ring__arc');
    if (!arcs.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var arc = entry.target;
        var target = parseFloat(arc.getAttribute('data-rv-target'));
        if (!isNaN(target)) {
          animateRingArc(arc, target);
        }
        io.unobserve(arc);
      });
    }, { threshold: 0.35 });

    function measureAndSet() {
      arcs.forEach(function (arc) {
        var current = arc.getAttribute('stroke-dasharray');
        if (!current || arc.getAttribute('data-rv-target')) return;
        // Liquid renders e.g. "288.4 339.3" — the first number is the target arc length.
        var targetLength = parseFloat(current);
        if (isNaN(targetLength)) return;
        arc.setAttribute('data-rv-target', targetLength);

        if (isAlreadyOnScreen(arc)) {
          // Already on screen at settled load (e.g. reload mid-scroll): draw
          // straight to the correct value, matching initReveal()'s pattern —
          // never flash to a near-empty dot for content the user is already
          // looking at.
          arc.setAttribute('stroke-dasharray', targetLength + ' ' + RING_CIRCUMFERENCE);
        } else {
          arc.setAttribute('stroke-dasharray', '0 ' + RING_CIRCUMFERENCE);
          io.observe(arc);
        }
      });
    }

    // Measure against final, settled layout (see FIX Aug 2026 above) rather
    // than at script-init time. If the page has already fully loaded by the
    // time this runs, there's nothing to wait for.
    if (document.readyState === 'complete') {
      measureAndSet();
    } else {
      window.addEventListener('load', measureAndSet, { once: true });
    }
  }

  /* ----------------------------------------------------------------
     Judge.me star-icon font (Aug 2026 — tofu boxes instead of stars,
     PDP only, sales page fine).

     MECHANISM, confirmed live via DevTools on both templates (not
     guessed): the star glyphs — hero preview badge AND review
     carousel — render through a custom icon font. On the sales page,
     the computed font-family on .jdgm-star is "JudgemeStar" and the
     glyph renders as a star. On the PDP it doesn't resolve to that
     font at all, so the glyph falls back to the theme body font,
     which has no character at that code point: a tofu box.

     Confirmed via each page's Network tab: both templates load
     widget_v3_base.css (checked its contents directly — it declares
     Judge.me's "JudgemeIcons" font, used for badges/UI glyphs, and
     nothing named "JudgemeStar"). Only the sales page additionally
     loads widget_v3_main.css. That's triggered by the Review Widget
     app block (sorenna-sales-reviews-cta.liquid), which exists only
     on the sales page. The star font — @font-face and the rule that
     assigns it to .jdgm-star — lives in that main bundle, so it's
     only ever registered on pages that load it.

     FIX: don't add the Review Widget block to the PDP just to force
     main.css to load — that's a content/structural change, not a
     rendering fix, and outside what this section owns. Instead, once
     the page is idle, check whether the JudgemeStar font is actually
     available. If it is (this page already loads widget_v3_main.css,
     as the sales page does), do nothing — no duplicate request. If
     it isn't, find Judge.me's own widget_v3_base.css <link> that's
     already on every page regardless of template, and request its
     "main" sibling from that exact same host/path/querystring Judge.me
     is already using — never a hardcoded URL, so this tracks
     Judge.me's own CDN if they ever restructure it, and is inert if
     Judge.me ever renames the bundle (the link simply isn't found and
     nothing is requested — no error, no regression). Loads only a
     stylesheet; renders no widget, adds no visible content.
     ---------------------------------------------------------------- */
  function initJudgeMeStarFont() {
    if (window.Shopify && window.Shopify.designMode) return;
    if (!document.querySelector('.jdgm-star')) return;
    if (!('fonts' in document)) return;

    function ensureFont() {
      document.fonts.ready.then(function () {
        if (document.fonts.check('1em JudgemeStar')) return; // already available, nothing to do

        var baseLink = document.querySelector('link[href*="widget_v3_base.css"]');
        if (!baseLink || !baseLink.href) return;
        var mainHref = baseLink.href.replace('widget_v3_base.css', 'widget_v3_main.css');
        if (mainHref === baseLink.href) return; // replace found nothing to swap; bail rather than reload base.css
        if (document.querySelector('link[href="' + mainHref + '"]')) return; // already requested

        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = mainHref;
        document.head.appendChild(link);
      });
    }

    if (document.readyState === 'complete') {
      ensureFont();
    } else {
      window.addEventListener('load', ensureFont, { once: true });
    }
  }

  function init() {
    initReveal();
    initRings();
    initJudgeMeStarFont();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
